import { type ComponentType, type LazyExoticComponent, lazy } from 'react'
import type { SecretName } from '@/auth/secrets'
import type { SecretDrawerProps } from '@/components/secret-drawer'
import type { BackfillDrawerProps } from '@/components/settings/backfill-drawer'
import type { R2DrawerProps } from '@/components/settings/r2-drawer'
import type { RenameAudienceProps } from '@/jobs/rename-dialog'
import type { TokenDialogProps } from '@/keys/key-overlays'
import type { RenameReelProps } from '@/reels/rename-dialog'
import type { CookieDrawerProps } from '@/signals/cookie-drawer'
import type { DeleteSourceProps } from '@/signals/source-actions'
import type { DiscoverTopicsProps } from '@/topics/discover-drawer'
import { ConfirmDialog, type ConfirmOptions } from './confirm'

/**
 * Every dialog and drawer in the app, by id — and the only way one opens
 * (#209). Each entry is a dynamic import, so Vite splits the content into its
 * own chunk, fetched the first time it opens (or on hover of its opener, see
 * `useOpener`). Nothing here runs at page load but `lazy()`.
 *
 * To add one: write the content (it renders its panel's inside — a
 * `FormDrawer`, a `ConfirmDialog`, a header and body — and takes its props plus
 * `close`), declare it in `Overlays` below, and add an entry to `entries`. The
 * shell (portal, backdrop, focus trap, Escape, focus return, enter/exit,
 * stacking) is the host's job, not the content's. An id this map does not know,
 * or a prop its content does not take, is a compile error at the
 * `overlays.open` / `overlays.push` call site.
 */

/**
 * One overlay's types: the props it opens with and the result it can hand back
 * to whoever `push`ed it (ADR-0019 D6). Most hand back nothing (`never`).
 */
export interface Overlay<Props, Result = never> {
  props: Props
  result: Result
}

/** Every overlay by id: its props and its result, declared once, here. */
export interface Overlays {
  confirm: Overlay<ConfirmOptions>
  palette: Overlay<Record<never, never>>
  'garmin-backfill': Overlay<BackfillDrawerProps>
  'r2-connector': Overlay<R2DrawerProps>
  'key-create': Overlay<Record<never, never>>
  'key-token': Overlay<TokenDialogProps>
  /** Resolves with the secret it saved (#230); an agent account hands back nothing. */
  'secret-editor': Overlay<SecretDrawerProps, SecretName>
  'audience-rename': Overlay<RenameAudienceProps>
  'audience-new': Overlay<Record<never, never>>
  'reel-rename': Overlay<RenameReelProps>
  'reel-new': Overlay<Record<never, never>>
  'delete-source': Overlay<DeleteSourceProps>
  'signals-add': Overlay<Record<never, never>>
  'topics-discover': Overlay<DiscoverTopicsProps>
  /** Resolves with the secret name the cookie was saved under. */
  'substack-cookie': Overlay<CookieDrawerProps, string>
}

export type OverlayId = keyof Overlays

export type OverlayPropsById = { [K in OverlayId]: Overlays[K]['props'] }

/** What `overlays.push(id)` resolves with when the content closes with a result. */
export type OverlayResult<K extends OverlayId> = Overlays[K]['result']

/** What every content component is handed besides its own props. */
export type OverlayContentProps<K extends OverlayId> = OverlayPropsById[K] & {
  /**
   * Closes THIS overlay (a no-op once it has gone). An overlay with a result
   * hands it to whoever pushed it; closed without one, the opener gets
   * `undefined`. One without a result takes no argument, so `onClick={close}`
   * reads as it always did.
   */
  close: [OverlayResult<K>] extends [never] ? () => void : (result?: OverlayResult<K>) => void
}

export interface OverlayMeta {
  /** Accessible name while the content is still loading (the content names itself after). */
  label: string
  /** `dialog` is centred; `drawer` is the right-hand panel, full height. */
  variant: 'dialog' | 'drawer'
  /** `alertdialog` for a confirm: announced as one, and a click outside does not dismiss it. */
  role?: 'dialog' | 'alertdialog'
  /** A dialog pinned near the top (search) rather than centred. */
  placement?: 'center' | 'top'
  /** The content paints its own surface (the palette); the host adds no panel or close button. */
  plain?: boolean
  /**
   * A drawer's width. `standard` (the default) is every form drawer's; `wide`
   * is for a form that needs two columns on a desktop (New reel, #232). The
   * host turns it into the panel's classes, so no content sets its own width.
   */
  size?: 'standard' | 'wide'
}

type Loader<K extends OverlayId> = () => Promise<{
  default: ComponentType<OverlayContentProps<K>>
}>

type Entry<K extends OverlayId> = { load: Loader<K>; meta: OverlayMeta }

const entries: { [K in OverlayId]: Entry<K> } = {
  // The one entry in the eager bundle: a confirm is a few hundred bytes, and a
  // skeleton flashing before "Delete this?" would cost more than it saves.
  confirm: {
    load: () => Promise.resolve({ default: ConfirmDialog }),
    meta: { label: 'Confirm', variant: 'dialog', role: 'alertdialog' },
  },
  palette: {
    load: () => import('@/components/command-palette').then((m) => ({ default: m.CommandPalette })),
    meta: { label: 'Command palette', variant: 'dialog', placement: 'top', plain: true },
  },
  'garmin-backfill': {
    load: () =>
      import('@/components/settings/backfill-drawer').then((m) => ({ default: m.BackfillDrawer })),
    meta: { label: 'Pull Garmin history', variant: 'drawer' },
  },
  'r2-connector': {
    load: () => import('@/components/settings/r2-drawer').then((m) => ({ default: m.R2Drawer })),
    meta: { label: 'Cloudflare R2', variant: 'drawer' },
  },
  'key-create': {
    load: () => import('@/keys/key-overlays').then((m) => ({ default: m.CreateKeyDrawer })),
    meta: { label: 'Create a key', variant: 'drawer' },
  },
  'key-token': {
    load: () => import('@/keys/key-overlays').then((m) => ({ default: m.TokenDialog })),
    meta: { label: 'Key created', variant: 'dialog' },
  },
  'secret-editor': {
    load: () => import('@/components/secret-drawer').then((m) => ({ default: m.SecretDrawer })),
    meta: { label: 'Secret', variant: 'drawer' },
  },
  'audience-rename': {
    load: () => import('@/jobs/rename-dialog').then((m) => ({ default: m.RenameAudienceDialog })),
    meta: { label: 'Rename audience', variant: 'dialog' },
  },
  'audience-new': {
    load: () =>
      import('@/jobs/new-audience-drawer').then((m) => ({ default: m.NewAudienceDrawer })),
    meta: { label: 'New audience', variant: 'drawer' },
  },
  'reel-rename': {
    load: () => import('@/reels/rename-dialog').then((m) => ({ default: m.RenameReelDialog })),
    meta: { label: 'Rename reel', variant: 'dialog' },
  },
  'reel-new': {
    load: () => import('@/reels/new-reel-drawer').then((m) => ({ default: m.NewReelDrawer })),
    // Wide: each step lays its fields in two columns on a desktop (#232).
    meta: { label: 'New reel', variant: 'drawer', size: 'wide' },
  },
  'delete-source': {
    load: () => import('@/signals/source-actions').then((m) => ({ default: m.DeleteSourceDialog })),
    meta: { label: 'Delete this source', variant: 'dialog', role: 'alertdialog' },
  },
  'signals-add': {
    load: () =>
      import('@/signals/add-signals-drawer').then((m) => ({ default: m.AddSignalsDrawer })),
    meta: { label: 'Add signals', variant: 'drawer' },
  },
  'topics-discover': {
    load: () =>
      import('@/topics/discover-drawer').then((m) => ({ default: m.DiscoverTopicsDrawer })),
    meta: { label: 'Discover topics', variant: 'drawer' },
  },
  'substack-cookie': {
    load: () => import('@/signals/cookie-drawer').then((m) => ({ default: m.CookieDrawer })),
    meta: { label: 'Add a Substack cookie', variant: 'drawer' },
  },
}

/** Content as the host renders it: the props union was checked at `open()`. */
export type AnyContent = ComponentType<Record<string, unknown>>

/**
 * Content whose chunk has already landed. Rendering it directly, rather than
 * through `lazy()`, skips the one suspended render `lazy()` always costs — so
 * an overlay preloaded on hover opens with its content, not its skeleton.
 */
const ready: Partial<Record<OverlayId, AnyContent>> = {
  confirm: ConfirmDialog as unknown as AnyContent,
}

function load(id: OverlayId) {
  return (entries[id].load as Loader<OverlayId>)().then((m) => {
    ready[id] = m.default as unknown as AnyContent
    return { default: m.default as unknown as AnyContent }
  })
}

const lazies = Object.fromEntries(
  (Object.keys(entries) as OverlayId[]).map((id) => [id, lazy(() => load(id))]),
) as Record<OverlayId, LazyExoticComponent<AnyContent>>

/**
 * The component to render for an overlay: the loaded one if its chunk is in,
 * else the lazy one. Looked up here, outside any component body, and chosen
 * once per open by the host, so it never swaps type under an open panel.
 */
export function overlayContent(id: OverlayId): AnyContent {
  return ready[id] ?? lazies[id]
}

export function overlayMeta(id: OverlayId): OverlayMeta {
  return entries[id].meta
}

/** Start fetching an overlay's chunk. The browser dedupes an import already loaded or in flight. */
export function preloadEntry(id: OverlayId): void {
  if (!ready[id]) void load(id)
}

/** Fetch an overlay's chunk and resolve once it is in, so the next open renders its content at once. */
export async function loadEntry(id: OverlayId): Promise<void> {
  if (!ready[id]) await load(id)
}
